import jsLogger from 'js-logger'

const MILLISECOND = 1000
const PREFERRED_SAMPLE_RATE = 16000
const PACKET_DURATION = 80 // ms - chunk size
const BUFFER_SIZE = 2048
const STEREO_FLOAT_TO_MONO_INT16 = 0x10000 / 4 - 1

// Provider-neutral microphone capturer. Opens an AudioContext on the given mic
// stream, downmixes to mono linear16 at (preferably) 16kHz, and hands each raw
// PCM frame to `onFrame`. It transcribes NOTHING itself — server-side STT
// consumes the frames (streamed to the socket by the caller), so the engine
// (Deepgram or otherwise) lives entirely on the server. This is the capture half
// of the old DeepgramTranscriber, split out so the server-STT path carries no
// provider WS or result-parsing code.
class PCMAudioCapture {
  private _stream: MediaStream
  private _onFrame: (pcm: ArrayBuffer, sampleRate: number) => void
  private _source?: MediaStreamAudioSourceNode
  private _recorderNode?: AudioWorkletNode
  private _audioContext?: AudioContext
  private _sampleRate = PREFERRED_SAMPLE_RATE
  private _stopped = false

  constructor(
    stream: MediaStream,
    onFrame: (pcm: ArrayBuffer, sampleRate: number) => void
  ) {
    this._stream = stream
    this._onFrame = onFrame
    this.start()
  }

  start = async () => {
    jsLogger.debug('PCMAudioCapture: start')
    this._audioContext = new AudioContext({ sampleRate: PREFERRED_SAMPLE_RATE })
    try {
      this._source = this._audioContext.createMediaStreamSource(this._stream)
    } catch (e) {
      // Some browsers can't return an arbitrary audio sample rate (e.g. Firefox).
      this._audioContext = new AudioContext()
      this._source = this._audioContext.createMediaStreamSource(this._stream)
    }
    const packetSamples =
      (this._audioContext.sampleRate * PACKET_DURATION) / MILLISECOND
    let port: any
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
    if (isSafari) {
      const recorder = this._audioContext.createScriptProcessor(BUFFER_SIZE)
      this._source.connect(recorder)
      port = {}
      let queueLength = 0
      const start = Date.now()
      let num = 0
      const queue = new Int16Array(packetSamples)
      recorder.onaudioprocess = function (e) {
        if (port.onmessage) {
          const left = e.inputBuffer.getChannelData(0)
          const right = e.inputBuffer.getChannelData(1) || left
          if (left && right) {
            for (let i = 0; i < left.length; i++) {
              queue[queueLength++] =
                (left[i] + right[i]) * STEREO_FLOAT_TO_MONO_INT16
              if (queueLength === queue.length) {
                queueLength = 0
                port.onmessage({
                  data: {
                    data: queue,
                    log: [Date.now(), Date.now() - start, ++num]
                  }
                })
              }
            }
          }
        }
      }
      recorder.connect(this._audioContext.destination)
    } else {
      const blob = new Blob(
        [
          `
registerProcessor("pcm-processor", class extends AudioWorkletProcessor {
  constructor(options) {
    super();
    this.start=Date.now();
	this.num=0;
	this.queue=new Int16Array(${packetSamples});
	this.queueLength=0;
  }
  process(input) {
    const left = input[0][0];
    const right = input[0][1]||left;
	if(left && right){
		for(let i=0;i<left.length;i++){
			this.queue[this.queueLength++]=(left[i]+right[i])*${STEREO_FLOAT_TO_MONO_INT16};
			if(this.queueLength===this.queue.length){
					this.queueLength=0;
					this.port.postMessage({data:this.queue,log:[Date.now(),Date.now()-this.start,++this.num]});
			}
		}
	}
    return true;
  }
});`
        ],
        { type: 'application/javascript; charset=utf-8' }
      )
      const url = URL.createObjectURL(blob)
      await this._audioContext.audioWorklet.addModule(url)
      this._recorderNode = new AudioWorkletNode(
        this._audioContext,
        'pcm-processor'
      )
      this._recorderNode.connect(this._audioContext.destination)
      port = this._recorderNode.port
      this._source.connect(this._recorderNode)
    }

    port.onmessage = ({ data }: { data: any }) => {
      if (this._stopped) return
      // Copy the frame (the worklet reuses the underlying buffer) and hand it to
      // the caller. Fire-and-forget.
      this._onFrame(
        data.data.buffer.slice(0),
        this._audioContext?.sampleRate ?? PREFERRED_SAMPLE_RATE
      )
    }

    this._sampleRate = this._audioContext.sampleRate
  }

  get sampleRate() {
    return this._sampleRate
  }

  stop = () => {
    jsLogger.log('PCMAudioCapture: stop')
    this._stopped = true
    this._source && this._source.disconnect()
    this._recorderNode && this._recorderNode.disconnect()
    this._audioContext &&
      this._audioContext.state !== 'closed' &&
      this._audioContext.close()
  }
}

export default PCMAudioCapture
